import React, { useEffect, useState } from "react";
import {
  FaFilePdf,
  FaEye,
  FaDownload,
  FaPenToSquare,
  FaCheck,
  FaX,
  FaCirclePlus,
  FaTrashCan,
} from "react-icons/fa6";
import "../../pages/user/css/Curriculum.css";
import { API_ciudadanos, API_BASE_URL } from "../../config/apiConfig";
import { useAuth } from "../../context/AuthContext";

const Documentos = ({ usuario }) => {
  const { token } = useAuth();

  const [documentos, setDocumentos] = useState([
    // static example data
    // { id: 1, nombre: "DUDASPASnameAPORTE.pdf", espacio: '', url: "/pdf/DUDASPASAPORTE.pdf" },
  ]);

  // Estados de edición
  const [editingId, setEditingId] = useState(null);
  const [nuevoNombre, setNuevoNombre] = useState("");
  const [nuevoEspacio, setNuevoEspacio] = useState("");

  useEffect(() => {
    if (usuario?.userId) {
      fetch(`${API_ciudadanos}/${usuario.userId}/files`, {
        headers: { "x-auth-token": token },
      })
        .then((res) => res.json())
        .then((data) => {
          if (!Array.isArray(data)) {
            setDocumentos([]);
            console.warn("No se encontraron documentos para el usuario.");
            return;
          }
          setDocumentos(
            data.map((d) => ({
              id: d.fileId,
              nombre: d.filename,
              url: `${API_BASE_URL}/downloads/ciudadanos/${d.fileId}`,
              espacio: d.folder || "",
            }))
          );
        })
        .catch((err) => console.error("Error al cargar competencias:", err));
    }
  }, [usuario]);

  // Previsualizar documento
  const handleView = async (url) => {
    try {
      const response = await fetch(url, {
        headers: { "x-auth-token": token },
      });
      // Convert to blob to create object URL
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      window.open(objectUrl, "_blank");
    } catch (error) {
      console.error("Error fetching document:", error);
    }
  };

  // Descargar pdf
  const handleDownload = async (url, nombre) => {
    try {
      const response = await fetch(url, {
        headers: { "x-auth-token": token },
      });
      // Convert to blob
      const blob = await response.blob();
      // Create object URL and trigger download
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = nombre;
      link.click();
      // Clean up the object URL after download
      setTimeout(() => URL.revokeObjectURL(objectUrl), 100);
    } catch (error) {
      console.error("Error downloading document:", error);
    }
  };

  // Habilitar modo edición
  const handleEditClick = (id, nombre, espacio) => {
    const nombreSinExtension = nombre.replace(/\.[^.]+$/, "");
    setEditingId(id);
    setNuevoNombre(nombreSinExtension);
    setNuevoEspacio(espacio || "");
  };

  const handleNombreChange = (e) => {
    setNuevoNombre(e.target.value);
  };
  const handleEspacioChange = (e) => {
    setNuevoEspacio(e.target.value);
  };

  // Guardar cambios
  const handleSaveEdit = async (id) => {
    try {
      const docObj = documentos.find((doc) => doc.id === id);
      const fileExt = docObj.nombre.split(".").pop();
      const response = await fetch(
        `${API_ciudadanos}/${usuario.userId}/files/${id}`,
        {
          method: "PUT",
          headers: {
            "x-auth-token": token,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            newFilename: `${nuevoNombre}.${fileExt}`,
            newFolderName: nuevoEspacio,
          }),
        }
      );

      if (response.ok) {
        setDocumentos((prev) =>
          prev.map((doc) =>
            doc.id === id
              ? {
                  ...doc,
                  nombre: `${nuevoNombre}.${fileExt}`,
                  espacio: nuevoEspacio,
                }
              : doc
          )
        );
      } else {
        throw new Error("El servidor respondio un error " + response.status);
      }
      setEditingId(null);
    } catch (error) {
      console.error("Error al guardar cambios:", error);
      alert("Error al guardar cambios: " + error.message);
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setNuevoNombre("");
    setNuevoEspacio("");
  };

  // Función para subir un archivo
  const handleFileUpload = async (event) => {
    try {
      if (!event.target.files || event.target.files.length === 0) return;

      const file = event.target.files[0];
      if (!file) return;

      // Create form data for multipart/form-data upload
      const formData = new FormData();
      formData.append("file", file);
      formData.append("folder", "otros"); // Default folder name
      formData.append("userId", usuario.userId); // Default folder name

      // Upload the file
      const response = await fetch(
        `${API_ciudadanos}/${usuario.userId}/files`,
        {
          method: "POST",
          headers: {
            "x-auth-token": token,
          },
          body: formData,
        }
      );

      if (response.ok) {
        const result = await response.json();
        if (!result.isOk || !result.fileIds?.[0])
          throw new Error(
            result.message || "Error desconocido al subir el archivo"
          );
        // Add the new file to the documents list
        setDocumentos((prev) => [
          ...prev,
          {
            id: result.fileIds?.[0],
            nombre: file.name,
            url: `${API_BASE_URL}/downloads/ciudadanos/${result.fileIds?.[0]}`,
            espacio: "otros",
          },
        ]);
      } else {
        throw new Error("Error al subir el archivo: " + response.status);
      }
    } catch (error) {
      console.error("Error al subir el archivo:", error);
      alert("Error al subir el archivo: " + error.message);
    }
  };

  const handleDelete = async (id) => {
    if (
      !window.confirm(
        "¿Estás seguro de que quieres eliminar este documento? Esta acción no se puede deshacer."
      )
    ) {
      return;
    }

    try {
      const response = await fetch(
        `${API_ciudadanos}/${usuario.userId}/files/${id}`,
        {
          method: "DELETE",
          headers: {
            "x-auth-token": token,
          },
        }
      );

      if (response.ok) {
        setDocumentos((prev) => prev.filter((doc) => doc.id !== id));
      } else {
        throw new Error("Error al eliminar el documento: " + response.status);
      }
    } catch (error) {
      console.error("Error al eliminar el documento:", error);
      alert("Error al eliminar el documento: " + error.message);
    }
  };

  return (
    <div className="ocupaciones caja-verde">
      <div className="titulo-editar between blanco fondo-verde">
        <h2 className="align-center mayusculas">
          <FaFilePdf />
          Repositorio documental{" "}
        </h2>
        <label className="subir-documento fondo-verde blanco no-border">
          <FaCirclePlus className="icono-subir cursor" />
          <input
            type="file"
            accept="application/pdf"
            onChange={handleFileUpload}
            hidden
          />
        </label>
      </div>
      <ul className="contenido documentos">
        <li className="between titulo verde bold">
          <span className="w-70p">NOMBRE DEL DOCUMENTO</span>
          <span className="w-20p">CARPETA</span>
          <span className="w-10p"></span>
        </li>
        {documentos.map((doc) => (
          <li key={doc.id} className="between">
            {editingId === doc.id ? (
              <>
                {/* Input para edición */}
                <span className="w-70p">
                  <input
                    aria-label="Nombre del documento"
                    className="w-100"
                    type="text"
                    value={nuevoNombre}
                    onChange={handleNombreChange}
                  />
                </span>
                <span className="w-20p">
                  <input
                    aria-label="Espacio (carpeta) del documento"
                    className="w-100"
                    type="text"
                    value={nuevoEspacio}
                    onChange={handleEspacioChange}
                  />
                </span>
                <span className="check align-center iconos w-10p">
                  <button
                    aria-label="Guardar cambios"
                    className="fondo-blanco verde"
                    onClick={() => handleSaveEdit(doc.id)}
                  >
                    <FaCheck />
                  </button>
                  <button
                    aria-label="Cancelar cambios"
                    className="fondo-blanco rojo"
                    onClick={handleCancelEdit}
                  >
                    <FaX />
                  </button>
                </span>
              </>
            ) : (
              <>
                <span className="w-70p">{doc.nombre}</span>
                <span className="w-20p">{doc.espacio}</span>
                <span className="botones-docs align-center iconos w-10p">
                  <button
                    aria-label="Editar el nombre del documento"
                    className="fondo-blanco no-border"
                    onClick={() =>
                      handleEditClick(doc.id, doc.nombre, doc.espacio)
                    }
                  >
                    <FaPenToSquare />
                  </button>
                  {/* <button aria-label="Ver el documento" className="fondo-blanco no-border" onClick={() => handleView(doc.url)}><FaEye /></button> */}
                  <button
                    aria-label="Descargar el documento"
                    className="fondo-blanco no-border"
                    onClick={() => handleDownload(doc.url, doc.nombre)}
                  >
                    <FaDownload />
                  </button>
                  <button
                    aria-label="Eliminar documento"
                    className="fondo-blanco no-border"
                    onClick={() => handleDelete(doc.id)}
                  >
                    <FaTrashCan />
                  </button>
                </span>
              </>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default Documentos;
